import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../const';

const NO_RELATED_ENDPOINTS = {};

// Keep detail and selection data together so an incomplete load cannot erase associations.
export default function useRecordEditor({
  recordId, endpoint, label, relatedEndpoints = NO_RELATED_ENDPOINTS,
  onSuccess, onSubmittingChange,
}) {
  const [state, setState] = useState({ record: null, related: {}, loading: true, error: '' });
  const [reloadKey, setReloadKey] = useState(0);
  const lifecycle = useRef(null);
  const saving = useRef(false);
  const token = sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '');
  const matches = state.endpoint === endpoint && String(state.recordId) === String(recordId);
  const loading = !matches || state.loading;
  const loadError = matches ? state.error : '';
  const record = matches ? state.record : null;

  // Invalidate synchronously on close, before a pending request can finish.
  useLayoutEffect(() => () => {
    if (lifecycle.current) lifecycle.current.active = false;
  }, [recordId, endpoint, token]);

  useEffect(() => {
    const request = { active: true };
    lifecycle.current = request;
    const controller = new AbortController();
    const loadMessage = 'Unable to load ' + label + ' details. Please try again.';
    const config = {
      headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
      signal: controller.signal,
    };
    setState({ endpoint, recordId, record: null, related: {}, loading: true, error: '' });
    const load = async () => {
      try {
        const relatedEntries = Object.entries(relatedEndpoints);
        const [detail, ...relatedResponses] = await Promise.all([
          axios.get(API_URL + endpoint + '/' + recordId, config),
          ...relatedEntries.map(([, path]) => axios.get(API_URL + path, config)),
        ]);
        if (!request.active) return;
        if (!detail.data?.status || !detail.data.data ||
            String(detail.data.data.id) !== String(recordId)) {
          throw new Error((!detail.data?.status && detail.data?.message) || loadMessage);
        }
        const related = {};
        relatedResponses.forEach((response, index) => {
          if (!response.data?.status || response.data.data == null) {
            throw new Error((!response.data?.status && response.data?.message) || loadMessage);
          }
          related[relatedEntries[index][0]] = response.data.data;
        });
        setState({ endpoint, recordId, record: detail.data.data, related, loading: false, error: '' });
      } catch (error) {
        if (request.active) {
          setState({ endpoint, recordId, record: null, related: {}, loading: false,
            error: error.response?.data?.message ||
              (error.isAxiosError ? loadMessage : error.message) || loadMessage });
        }
      }
    };
    load();
    return () => { request.active = false; controller.abort(); };
  }, [recordId, endpoint, label, relatedEndpoints, reloadKey, token]);

  const submit = async (values, { setSubmitting, setStatus, setErrors }) => {
    if (saving.current) return;
    if (loading || loadError || !record || String(record.id) !== String(recordId)) {
      setSubmitting(false);
      return;
    }
    const request = lifecycle.current;
    saving.current = true;
    setStatus(null);
    onSubmittingChange?.(true);
    let result;
    try {
      const response = await axios.put(API_URL + endpoint + '/' + recordId, values, {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
      });
      if (!request?.active) return;
      if (!response.data?.status) {
        setStatus(response.data?.message || 'Failed to update ' + label + '.');
        return;
      }
      result = response.data;
    } catch (error) {
      if (!request?.active) return;
      const errors = error.response?.data?.errors;
      if (errors) {
        setErrors(Object.fromEntries(Object.entries(errors).map(([field, messages]) => [
          field, Array.isArray(messages) ? messages[0] : messages,
        ])));
      }
      setStatus(error.response?.data?.message || 'Failed to update ' + label + '.');
    } finally {
      saving.current = false;
      if (request?.active) {
        setSubmitting(false);
        onSubmittingChange?.(false);
      }
    }
    if (result && request?.active) onSuccess?.(result);
  };

  return {
    record, related: matches ? state.related : {}, loading, loadError,
    retry: () => setReloadKey(value => value + 1), submit,
  };
}
